import "./patient-search.scss";

import {
  GetPatientModel,
  GetResponsePatientModel,
} from "@factory/model/query-model-patient";
import { useQyGetPatient } from "@factory/query/patient.query";
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
} from "primereact/autocomplete";
import { useCallback, useState } from "react";

interface PatientSearchProps {
  onPatientSelect: (patient: GetPatientModel | null) => void;
}

interface PatientOption {
  label: string;
  value: string;
  patient: GetPatientModel;
}

export function PatientSearch({ onPatientSelect }: PatientSearchProps) {
  const [value, setValue] = useState<PatientOption | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState<PatientOption[]>([]);
  const [enabledGet, setEnabledGet] = useState(false);

  // Query for patients
  const handleSuggestions = (response: GetResponsePatientModel) => {
    const mappedSuggestions = (response?.data || []).map(
      (item: GetPatientModel) =>
        ({
          label: `${item.last_name}, ${item.first_name} ${item.middle_name || ""}`.trim(),
          value: item.code,
          patient: item,
        } as PatientOption)
    );
    setSuggestions(mappedSuggestions);
    setEnabledGet(false);
  };

  useQyGetPatient(
    searchTerm,
    50,
    0,
    undefined,
    undefined,
    enabledGet,
    handleSuggestions
  );

  const onSearch = useCallback((event: AutoCompleteCompleteEvent) => {
    setSearchTerm(event.query);
    setEnabledGet(true);
  }, []);

  const handleSelect = useCallback(
    (selected: PatientOption | null) => {
      setValue(selected);
      if (selected) {
        onPatientSelect(selected.patient);
      } else {
        onPatientSelect(null);
      }
    },
    [onPatientSelect]
  );

  const itemTemplate = (option: PatientOption) => {
    const patient = option.patient;
    return (
      <div className="patient-item p-2">
        <div className="font-medium">{option.label}</div>
        <div className="text-sm text-gray-500">
          {patient.mobile_number && (
            <span className="mr-3">
              <i className="pi pi-phone text-xs mr-1"></i>
              {patient.mobile_number}
            </span>
          )}
          {patient.email && (
            <span>
              <i className="pi pi-envelope text-xs mr-1"></i>
              {patient.email}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div id="PatientSearch" className="patient-search">
      <AutoComplete
        id="patient-search-input"
        field="label"
        inputClassName="w-full"
        className="w-full"
        placeholder="Type patient name to search..."
        value={value}
        suggestions={suggestions}
        onChange={(e) => handleSelect(e.value)}
        completeMethod={onSearch}
        forceSelection={true}
        itemTemplate={itemTemplate}
        dropdown
        dropdownIcon="pi pi-search"
      />
      <small className="text-gray-400 block mt-1">
        Search by patient name, then select from results
      </small>
    </div>
  );
}

export default PatientSearch;
